import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { updateScenario, exportExcelURL, exportPdfURL } from "@/lib/api";
import { Percent, TrendingUp, TrendingDown, FileSpreadsheet, FileDown, Lock } from "lucide-react";
import { toast } from "sonner";

export default function GlobalControls({ scenario, onChange }) {
  const isBaseline = scenario.is_baseline;
  const [markup, setMarkup] = useState(scenario.global_markup_pct || 0);
  const [discount, setDiscount] = useState(scenario.global_discount_pct || 0);
  const [vat, setVat] = useState(scenario.vat_enabled ?? true);

  useEffect(() => {
    setMarkup(scenario.global_markup_pct || 0);
    setDiscount(scenario.global_discount_pct || 0);
    setVat(scenario.vat_enabled ?? true);
  }, [scenario.id, scenario.global_markup_pct, scenario.global_discount_pct, scenario.vat_enabled]);

  const persist = async (patch) => {
    try {
      await updateScenario(scenario.id, patch);
      onChange();
    } catch { toast.error("Failed to update"); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-6 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <TrendingDown className="h-3 w-3" /> Global Discount
          </Label>
          <div className="mt-2 relative">
            <Input
              type="number"
              step="0.5"
              value={discount}
              onChange={e => setDiscount(parseFloat(e.target.value) || 0)}
              onBlur={() => persist({ global_discount_pct: discount })}
              className="pr-8 num"
              data-testid="input-global-discount"
            />
            <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          </div>
        </div>

        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <TrendingUp className="h-3 w-3" /> Global Markup
          </Label>
          <div className="mt-2 relative">
            <Input
              type="number"
              step="0.5"
              value={markup}
              onChange={e => setMarkup(parseFloat(e.target.value) || 0)}
              onBlur={() => persist({ global_markup_pct: markup })}
              className="pr-8 num"
              data-testid="input-global-markup"
            />
            <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-lg border border-border bg-background/50 px-4 py-2.5">
          <Switch
            checked={vat}
            onCheckedChange={(v) => { setVat(v); persist({ vat_enabled: v }); }}
            data-testid="toggle-vat"
            id="vat-switch"
          />
          <Label htmlFor="vat-switch" className="cursor-pointer">
            <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">UAE VAT</div>
            <div className="text-sm font-semibold">{vat ? "5% Applied" : "Excluded"}</div>
          </Label>
        </div>

        <div className="flex items-center gap-2 md:ml-auto">
          <Button variant="outline" size="sm" asChild data-testid="btn-export-excel">
            <a href={exportExcelURL(scenario.id)} target="_blank" rel="noreferrer">
              <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
            </a>
          </Button>
          <Button variant="outline" size="sm" asChild data-testid="btn-export-pdf">
            <a href={exportPdfURL(scenario.id)} target="_blank" rel="noreferrer">
              <FileDown className="h-4 w-4 mr-1" /> PDF
            </a>
          </Button>
        </div>
      </div>

      {isBaseline && (
        <div className="mt-3 flex items-center gap-2 text-xs text-primary/80">
          <Lock className="h-3 w-3" /> Reference baseline — comparisons are calculated against this scenario. Clone it if you want to try edits without changing the baseline.
        </div>
      )}
    </div>
  );
}
